import * as React from 'react'

import { CircleCheckBigIcon, Trash2Icon, XIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import TagsSelector from '~/client/src/desktop/components/TagsSelector/TagsSelector'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import { Content, Footer, Header } from '~/client/src/shared/components/Layout'
import { Loader } from '~/client/src/shared/components/Loader'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { SidePanel } from '~/client/src/shared/components/SidePanel/SidePanel'
import { TagType } from '~/client/src/shared/enums/TagType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'

import { CompanyStatusAction } from '../types'
import BulkEditCompaniesSideBarStore from './BulkEditCompaniesSideBar.store'

import Colors from '~/client/src/shared/theme.module.scss'

interface IProps {
  selectedCompanies: Company[]
  onClose: () => void
  saveCompanies: (items: Company[]) => void
  loading: boolean
  errorMessage?: string
  statusAction: CompanyStatusAction | null

  toggleDeleteDialog: (isShow: boolean) => void
  tagsStore?: TagsStore
}

const availableTypes = [TagType.CompanyType, TagType.Role, TagType.Trade]
const requiredTypes = [TagType.CompanyType]

@inject('tagsStore')
@observer
export default class BulkEditCompaniesSideBar extends React.Component<IProps> {
  private readonly store: BulkEditCompaniesSideBarStore = null

  public constructor(props: IProps) {
    super(props)

    this.store = new BulkEditCompaniesSideBarStore(
      props.tagsStore,
      props.selectedCompanies,
      props.saveCompanies,
      availableTypes,
      requiredTypes,
    )
  }

  public componentDidMount() {
    const { onClose, selectedCompanies } = this.props

    if (!selectedCompanies.length) {
      onClose()
    }
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    const { onClose, selectedCompanies } = this.props

    if (!selectedCompanies.length) {
      onClose()
    }

    if (selectedCompanies !== prevProps.selectedCompanies) {
      this.store.init(selectedCompanies)
    }
  }

  public render() {
    const content = this.props.loading
      ? this.renderLoader()
      : this.renderContent()

    return <SidePanel>{content}</SidePanel>
  }

  private renderContent(): JSX.Element {
    const { onClose, errorMessage } = this.props
    const {
      changeSelection,
      defaultSelection,
      handleApply,
      canApply,
      isConfirmationModalOpened,
      closeConfirmationModal,
      onConfirm,
    } = this.store

    return (
      <>
        <Header className="tw:flex tw:flex-col tw:p-2.5 tw:px-5 tw:border-0 tw:border-b tw:border-solid tw:border-border tw:grow-0">
          <div className="tw:flex tw:items-center">
            <XIcon
              size={16}
              className="tw:grow-0 tw:cursor-pointer tw:mr-3"
              color={Colors.neutral60}
              onClick={onClose}
            />
            <span className="tw:flex-1 tw:text-title-md tw:text-foreground">
              {Localization.translator.edit_verb}
            </span>
            <Button
              className="bold"
              variant="default"
              disabled={!canApply}
              onClick={handleApply}
            >
              {Localization.translator.apply}
            </Button>
          </div>
          <div className="tw:text-body-md tw:text-primary tw:py-[5px]">
            {this.selectedCompaniesLabel}
          </div>
        </Header>
        <Content className="tw:px-5">
          <TagsSelector
            onChange={changeSelection}
            availableTypes={availableTypes}
            initSelection={defaultSelection}
          />
          {this.renderActionButtons()}
        </Content>
        <Footer>
          <div className="tw:text-title-sm tw:text-center tw:text-destructive tw:pb-6">
            {errorMessage}
          </div>
        </Footer>
        <ConfirmDialog
          isOpen={isConfirmationModalOpened}
          title={Localization.translator.confirmChange}
          onCancelClicked={closeConfirmationModal}
          onDoneClicked={onConfirm}
          doneTitle={Localization.translator.apply}
        >
          <div className="tw:text-body-md tw:text-foreground">
            {Localization.translator.emptyTagTypes(this.store.tagsWithErrors)}
          </div>
        </ConfirmDialog>
      </>
    )
  }

  private renderActionButtons() {
    const { toggleDeleteDialog, statusAction } = this.props
    const isActivationMode = statusAction === CompanyStatusAction.Activate

    return (
      <div className="tw:flex tw:flex-col">
        <div className="tw:border-0 tw:border-t tw:border-solid tw:border-border tw:mb-2.5" />
        <Button
          className="tw:min-w-[120px] tw:max-w-[120px] tw:mb-2.5"
          variant="primary-outline"
          disabled={!statusAction}
          onClick={() => toggleDeleteDialog(true)}
        >
          {isActivationMode ? (
            <CircleCheckBigIcon data-icon="inline-start" />
          ) : (
            <Trash2Icon data-icon="inline-start" />
          )}
          {isActivationMode
            ? Localization.translator.activate
            : Localization.translator.deactivate}
        </Button>
      </div>
    )
  }

  private renderLoader(): JSX.Element {
    return (
      <div className="tw:h-full tw:w-full tw:absolute modal-overlay">
        <Loader />
      </div>
    )
  }

  private get selectedCompaniesLabel(): string {
    return Localization.translator.xCompaniesSelected(
      this.props.selectedCompanies.length,
    )
  }
}
